import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { lessonApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, BookOpen, ExternalLink, Target, Clock, Users, Calendar, FileText, Video, Image, Music } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Lesson } from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

export const LessonDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skill, setSkill] = useState<Skill | null>(null);

  const canEdit = hasPermission('edit', 'lesson');
  const canDelete = hasPermission('delete', 'lesson');

  // Use entity hook for fetching lesson
  const {
    data: lesson,
    isLoading,
    error,
    refetch,
  } = useEntity<Lesson>({
    fetchFn: (lessonId: string) => lessonApi.getById(lessonId),
    entityId: id,
    entityName: 'lesson',
    enabled: !!id,
  });

  // Fetch skill details if skillId exists
  useEffect(() => {
    if (!lesson?.skillId) {
      setSkill(null);
      return;
    }

    skillApi.getById(lesson.skillId)
      .then(setSkill)
      .catch((err) => {
        console.error('Error loading skill:', err);
        setSkill(null);
      });
  }, [lesson?.skillId]);

  const isSystemDefined = lesson?.lessonGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'lesson'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Lesson ID is required');
      await lessonApi.delete(id);
    },
    entityName: 'lesson',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/lessons';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this lesson?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading lesson..." />;
  }

  if (error || !lesson) {
    return <ErrorState error={error || new Error('Lesson not found')} onRetry={refetch} />;
  }

  const lessonId = lesson.id || '';

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'PUBLISHED': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getMediaIcon = (mediaType?: string) => {
    switch (mediaType) {
      case 'VIDEO': return <Video className="h-5 w-5" />;
      case 'IMAGE': return <Image className="h-5 w-5" />;
      case 'AUDIO': return <Music className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/lessons">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-indigo-600" />
              <h1 className="text-3xl font-bold tracking-tight">{lesson.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              {lesson.status && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lesson.status)}`}>
                  {lesson.status}
                </span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(lesson.priority)}`}>
                {lesson.priority}
              </span>
              {lesson.skillLevel && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                  {lesson.skillLevel}
                </span>
              )}
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/lessons/${lessonId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core lesson details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={lessonId} label="Lesson ID" />
            {lesson.externalRefId && (
              <CopyableId id={lesson.externalRefId} label="External Reference ID" />
            )}
            {lesson.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{lesson.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                    {lesson.category.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Lesson Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                    {lesson.lessonType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            </div>
            {lesson.deliveryFrequency && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Delivery Frequency</label>
                <p className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{lesson.deliveryFrequency.replace(/_/g, ' ')}</span>
                </p>
              </div>
            )}
            {lesson.lessonGenerated && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Generated</label>
                <p className="mt-1 text-sm">{lesson.lessonGenerated.replace(/_/g, ' ')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Related Skill */}
        {lesson.skillId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Related Skill
              </CardTitle>
              <CardDescription>Skill associated with this lesson</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/skills/${lesson.skillId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {skill?.name || 'View Skill'}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <CopyableId id={lesson.skillId} label="Skill ID" />
                {skill?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                )}
                {skill?.category && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                    {skill.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery Settings */}
        {(lesson.deliveryChannels?.length || lesson.targetAudiences?.length) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Delivery Settings
              </CardTitle>
              <CardDescription>How and to whom the lesson is delivered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {lesson.deliveryChannels && lesson.deliveryChannels.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Delivery Channels</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {lesson.deliveryChannels.map((channel, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 text-sm"
                      >
                        {channel.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {lesson.targetAudiences && lesson.targetAudiences.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Target Audiences</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {lesson.targetAudiences.map((audience, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-sm"
                      >
                        {audience.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Schedule Information */}
        {(lesson.activationDate || lesson.expirationDate) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Schedule
              </CardTitle>
              <CardDescription>Activation and expiration dates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {lesson.activationDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Activation Date</label>
                    <p className="mt-1 text-sm">{new Date(lesson.activationDate).toLocaleString()}</p>
                  </div>
                )}
                {lesson.expirationDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Expiration Date</label>
                    <p className="mt-1 text-sm">{new Date(lesson.expirationDate).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {lesson.createdAt ? new Date(lesson.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {lesson.updatedAt ? new Date(lesson.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {lesson.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{lesson.createdBy}</p>
                </div>
              )}
              {lesson.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{lesson.updatedBy}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lesson Content */}
      {lesson.content && (lesson.content.headline || lesson.content.text || lesson.content.mediaUrl) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getMediaIcon(lesson.content.mediaType)}
              Lesson Content
            </CardTitle>
            <CardDescription>The main content of this lesson</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lesson.content.headline && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Headline</label>
                <p className="mt-1 text-lg font-semibold">{lesson.content.headline}</p>
              </div>
            )}
            {lesson.content.text && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Content Text</label>
                <div className="mt-2 p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{lesson.content.text}</p>
                </div>
              </div>
            )}
            {lesson.content.mediaUrl && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Media</label>
                <div className="mt-2 p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getMediaIcon(lesson.content.mediaType)}
                    <div>
                      <p className="text-sm font-medium">
                        {lesson.content.mediaType || 'Media'}
                      </p>
                      <a
                        href={lesson.content.mediaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        View Media <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Follow-up Prompts */}
      {lesson.followupPrompts && lesson.followupPrompts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Follow-up Prompts ({lesson.followupPrompts.length})</CardTitle>
            <CardDescription>Prompts to engage learners after the lesson</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lesson.followupPrompts.map((prompt, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-sm">{prompt}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {lesson.tags && lesson.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({lesson.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lesson.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LessonDetailPage;
